<div class="main-view space-y-2 md:space-y-4">
    <app-page-surface-header
        titleIcon="billing"
        title="Invoices"
        subtitle="Every invoice, its status and what is still owed."
    />

    <div class="grid gap-4 sm:grid-cols-3">
        <div class="panel">
            <p class="text-sm text-gray-500 dark:text-gray-400">Active Businesses</p>
            <p class="text-2xl font-bold">{{ dashboard.active_businesses }}</p>
        </div>
        <div class="panel">
            <p class="text-sm text-gray-500 dark:text-gray-400">Invoice Raised</p>
            <p class="text-2xl font-bold">{{ dashboard.invoice_raised }}</p>
        </div>
        <div class="panel">
            <p class="text-sm text-gray-500 dark:text-gray-400">Invoice Collected</p>
            <p class="text-2xl font-bold">{{ dashboard.invoice_collected }}</p>
        </div>
    </div>
    @if (dashboardError) {
    <p class="text-sm text-danger">{{ dashboardError }}</p>
    }

    @if (actionMsg) {
    <p class="rounded-lg bg-success/10 px-3 py-2 text-sm text-success">{{ actionMsg }}</p>
    }

    <ng-template #viewToggle>
        <fieldset class="m-0 inline-flex min-w-0 rounded-full border border-gray-300 bg-white p-0.5 dark:border-[#191e3a] dark:bg-[#0e1726]">
            <legend class="sr-only">Invoice view</legend>
            <button type="button" class="rounded-full px-4 py-1.5 text-sm font-medium"
                [class]="view === 'all' ? 'bg-[#303030] text-white' : 'text-gray-600 dark:text-gray-300'"
                [attr.aria-pressed]="view === 'all'" (click)="setView('all')">All invoices</button>
            <button type="button" class="rounded-full px-4 py-1.5 text-sm font-medium"
                [class]="view === 'clients' ? 'bg-[#303030] text-white' : 'text-gray-600 dark:text-gray-300'"
                [attr.aria-pressed]="view === 'clients'" (click)="setView('clients')">By client</button>
        </fieldset>
        <a routerLink="/billing/payments"
            class="btn inline-flex shrink-0 items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5">View payments →</a>
    </ng-template>

    @if (view === 'all') {
    <app-list-module-surface
        [config]="allSurfaceConfig"
        [filters]="listFilters"
        [loading]="loading"
        [errorMessage]="bannerError"
        [meta]="meta"
        [rowCount]="invoices.length"
        (filtersChange)="onListFiltersChange($event)"
        (pageChange)="onListPageChange($event)"
    >
        <ng-container toolbarActions>
            <ng-container *ngTemplateOutlet="viewToggle" />
        </ng-container>

        <ng-container toolbarFiltersExtra>
            <div class="relative">
                <select
                    id="invoice-client-filter"
                    class="form-select bg-white py-3 px-4 text-white-dark rounded-full border-white-light dark:border-[#191e3a] dark:bg-dark"
                    aria-label="Client"
                    [ngModel]="clientFilter"
                    (ngModelChange)="onClientChange($event)"
                >
                    <option [ngValue]="null">All clients</option>
                    @if (clientNotListed) {
                    <option [ngValue]="clientFilter">Client #{{ clientFilter }}</option>
                    }
                    @for (b of dashboard.businesses; track b.tenant_id) {
                    <option [ngValue]="b.tenant_id">{{ b.business }}</option>
                    }
                </select>
            </div>
        </ng-container>

        <app-list-module-table
            listTable
            variant="surface"
            [columns]="columns"
            [rows]="invoices"
            [loading]="loading"
            emptyMessage="No invoices found."
            rowTrackKey="id"
        >
            <ng-template listCellDef="number" let-row="row"><span class="font-mono text-xs">{{ row.invoice_number }}</span></ng-template>
            <ng-template listCellDef="client" let-row="row">{{ row.tenant?.tenant_name || ('Client #' + row.tenant_id) }}</ng-template>
            <ng-template listCellDef="issued" let-row="row"><span class="text-xs text-gray-500">{{ row.created_at | date:'dd MMM y' }}</span></ng-template>
            <ng-template listCellDef="due" let-row="row"><span class="text-xs text-gray-500">{{ row.due_date ? (row.due_date | date:'dd MMM y') : '—' }}</span></ng-template>
            <ng-template listCellDef="total" let-row="row">{{ row.currency }} {{ row.total | number:'1.2-2' }}</ng-template>
            <ng-template listCellDef="paid" let-row="row">{{ row.currency }} {{ row.paid_amount | number:'1.2-2' }}</ng-template>
            <ng-template listCellDef="balance" let-row="row"><span class="font-semibold">{{ row.currency }} {{ (row.balance ?? 0) | number:'1.2-2' }}</span></ng-template>
            <ng-template listCellDef="status" let-row="row">
                <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="statusOf(row).cls">{{ statusOf(row).label }}</span>
            </ng-template>
            <ng-template listCellDef="actions" let-row="row">
                <div class="flex flex-wrap justify-end gap-2">
                    <button type="button" class="rounded-lg border border-gray-300 px-2.5 py-1 text-xs text-black hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/5" (click)="previewInvoice(row)">Preview</button>
                    <button type="button" class="rounded-lg border border-gray-300 px-2.5 py-1 text-xs text-black hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/5" (click)="openHistory(row.id)">History</button>
                    @if (P.BILLING_MANAGE | hasPermission) {
                    @if (canSend(row)) {
                    <button type="button" class="rounded-lg border border-primary/40 px-2.5 py-1 text-xs text-primary" (click)="send(row)">Send</button>
                    }
                    @if (canRecordPayment(row)) {
                    <button type="button" class="rounded-lg bg-[#303030] px-2.5 py-1 text-xs font-medium text-white hover:bg-black" (click)="recordPayment(row)">Record payment</button>
                    }
                    @if (canVoid(row)) {
                    <button type="button" class="rounded-lg border border-danger/40 px-2.5 py-1 text-xs text-danger" (click)="voidInvoice(row)">Void</button>
                    }
                    }
                </div>
            </ng-template>
        </app-list-module-table>
    </app-list-module-surface>
    } @else {
    <app-list-module-surface
        [config]="clientsSurfaceConfig"
        [loading]="dashboardLoading"
        [errorMessage]="dashboardError"
        [rowCount]="dashboard.businesses.length"
    >
        <ng-container toolbarActions>
            <ng-container *ngTemplateOutlet="viewToggle" />
        </ng-container>

        <app-list-module-table
            listTable
            variant="surface"
            [columns]="clientColumns"
            [rows]="dashboard.businesses"
            [loading]="dashboardLoading"
            emptyMessage="No invoices yet."
            rowTrackKey="tenant_id"
        >
            <ng-template listCellDef="display_id" let-row="row">
                <button type="button" class="font-mono text-xs text-primary hover:underline" (click)="openBusiness(row.tenant_id)">{{ row.display_id }}</button>
            </ng-template>
            <ng-template listCellDef="total_invoices" let-row="row">{{ row.total_invoices }}</ng-template>
            <ng-template listCellDef="business" let-row="row">{{ row.business }}</ng-template>
            <ng-template listCellDef="total" let-row="row"><span class="font-semibold">₹ {{ row.total }}</span></ng-template>
            <ng-template listCellDef="issued_date" let-row="row">{{ row.issued_date ? (row.issued_date | date: 'dd MMM yyyy') : '—' }}</ng-template>
            <ng-template listCellDef="payment_type" let-row="row">{{ row.payment_type }}</ng-template>
            <ng-template listCellDef="paid_amount" let-row="row">₹ {{ row.paid_amount }}</ng-template>
            <ng-template listCellDef="balance" let-row="row">₹ {{ row.balance }}</ng-template>
        </app-list-module-table>
    </app-list-module-surface>
    }
</div>

@if (preview) {
<app-pdf-preview-modal [title]="preview.title" [filename]="preview.filename" [load]="preview.load" (closed)="closePreview()" />
}

<!-- Payment history -->
<div *ngIf="historyOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div class="panel max-h-[80vh] w-full max-w-2xl overflow-auto">
        <h5 class="mb-4 text-lg font-semibold">History</h5>
        <p *ngIf="historyLoadError" class="mb-4 text-sm text-danger">
            Could not load payment history for this invoice. Please try again.
        </p>
        <table class="table-hover" *ngIf="!historyLoadError">
            <thead>
                <tr>
                    <th>Sr. No.</th>
                    <th>Reference No.</th>
                    <th>Date of Receipt</th>
                    <th class="text-right">Amount Received</th>
                    <th class="text-right">TDS Amount</th>
                    <th class="text-right">Total</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let row of historyRows">
                    <td>{{ row.sr_no }}</td>
                    <td>{{ row.reference ?? '—' }}</td>
                    <td>{{ row.paid_date ? (row.paid_date | date: 'dd-MMM-yyyy') : '—' }}</td>
                    <td class="text-right">{{ row.amount_received | number: '1.2-2' }}</td>
                    <td class="text-right">{{ row.tds_amount | number: '1.2-2' }}</td>
                    <td class="text-right font-semibold">{{ row.total | number: '1.2-2' }}</td>
                </tr>
                <tr *ngIf="!historyRows.length">
                    <td colspan="6" class="text-center text-gray-500">No payments recorded.</td>
                </tr>
                <tr *ngIf="historyRows.length" class="font-semibold">
                    <td colspan="3">Grand Total</td>
                    <td class="text-right">{{ historyTotals.received | number: '1.2-2' }}</td>
                    <td class="text-right">{{ historyTotals.tds | number: '1.2-2' }}</td>
                    <td class="text-right">{{ historyTotals.total | number: '1.2-2' }}</td>
                </tr>
            </tbody>
        </table>
        <div class="mt-4 flex justify-end">
            <button type="button" class="btn rounded-full border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-black hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5" (click)="historyOpen = false">Close</button>
        </div>
    </div>
</div>
